import type {
  MultiLanguageChronicleItem,
  GetLocalizedDefaultItemsOptions,
  GetLocalizedItemsOptions,
  GetResult,
  ItemIdentifier,
  LocalizedChronicleItem,
  RequestOptions,
} from "../types";
import { resolveChronicleDomain } from "./domain";

function buildChronicleUrl(
  domain: string,
  pathParts: Array<string>,
  searchParams: Record<string, unknown> = {},
): URL {
  const baseUrl = `https://chronicle.vanilla.${domain}/api/v1`;
  const url = new URL(
    `${baseUrl}/${pathParts.map(encodeURIComponent).join("/")}`,
  );

  Object.entries(searchParams).forEach(([key, value]) => {
    if (value != null && value !== "") {
      url.searchParams.append(key, String(value));
    }
  });

  return url;
}

function createAbortError(): DOMException {
  return new DOMException("Aborted", "AbortError");
}

/**
 * Wires an AbortSignal to an XMLHttpRequest.
 *
 * - If the signal is already aborted, the request is aborted synchronously
 *   and the returned detach function is a no-op.
 * - Otherwise an `abort` listener is registered that aborts the XHR and
 *   rejects the promise with a fetch-compatible `AbortError` DOMException.
 * - The returned function must be called from every XHR terminal handler
 *   (`onload`, `onerror`, `ontimeout`, `onabort`) to avoid leaking the
 *   listener on long-lived signals.
 */
function attachAbortSignal(
  request: XMLHttpRequest,
  signal: AbortSignal | undefined,
  reject: (reason: unknown) => void,
): () => void {
  if (!signal) return () => {};
  if (signal.aborted) {
    request.abort();
    reject(createAbortError());
    return () => {};
  }
  const onAbort = () => {
    request.abort();
    reject(createAbortError());
  };
  signal.addEventListener("abort", onAbort, { once: true });
  return () => signal.removeEventListener("abort", onAbort);
}

export type PutItemParams = {
  chronicleItemId: string;
  item: MultiLanguageChronicleItem;
  productSlug: string;
  source?: string;
};

export function getDefaultItems(
  options?: GetLocalizedDefaultItemsOptions,
  { signal, country, env }: RequestOptions = {},
): Promise<Array<LocalizedChronicleItem>> {
  if (signal?.aborted) {
    return Promise.reject(createAbortError());
  }

  const searchParams = {
    language: options?.language || Check24.platform.language.get(),
    deviceoutput: options?.platformType,
  };

  const requestUrl = buildChronicleUrl(
    resolveChronicleDomain({ country, env }),
    ["default-items", "v1"],
    searchParams,
  );

  return new Promise<Array<LocalizedChronicleItem>>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("GET", requestUrl, true);
    request.withCredentials = true;

    const detach = attachAbortSignal(request, signal, reject);

    request.onload = function () {
      detach();
      if (this.status >= 200 && this.status < 400) {
        try {
          const json = JSON.parse(this.response as string);

          if (
            !Object.prototype.hasOwnProperty.call(
              json,
              "default_chronicle_items",
            )
          ) {
            reject(
              new Error(
                "Invalid response, default_chronicle_items does not exist",
              ),
            );
            return;
          }

          resolve(
            json.default_chronicle_items as Array<LocalizedChronicleItem>,
          );
        } catch (_e) {
          reject(new Error("Invalid JSON response"));
        }
      } else {
        reject(new Error(`HTTP ${this.status}: ${this.statusText}`));
      }
    };

    request.onerror = function () {
      detach();
      reject(new Error("Network error"));
    };

    request.ontimeout = function () {
      detach();
      reject(new Error("Request timeout"));
    };

    request.onabort = function () {
      detach();
    };

    request.send();
  });
}

export function getItems(
  options?: GetLocalizedItemsOptions,
  { signal, country, env }: RequestOptions = {},
): Promise<GetResult<LocalizedChronicleItem>> {
  if (signal?.aborted) {
    return Promise.reject(createAbortError());
  }

  return Check24.platform.auth.waitForDeviceToken().then(() => {
    if (signal?.aborted) {
      throw createAbortError();
    }

    const searchParams = {
      language: options?.language || Check24.platform.language.get(),
      activities_deviceoutput: options?.activitiesDeviceOutput,
      product_slug: Check24.platform.slug.get(),
      limit:
        options?.limit != null && typeof options.limit === "number"
          ? options.limit
          : undefined,
      cursor:
        options?.cursor != null && typeof options.cursor === "string"
          ? options.cursor
          : undefined,
      include_activities:
        options?.includeActivities === true ? "true" : undefined,
    };

    const requestUrl = buildChronicleUrl(
      resolveChronicleDomain({ country, env }),
      ["items", "v1"],
      searchParams,
    );

    return new Promise<GetResult<LocalizedChronicleItem>>((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open("GET", requestUrl, true);
      request.withCredentials = true;

      const detach = attachAbortSignal(request, signal, reject);

      request.onload = function () {
        detach();
        if (this.status >= 200 && this.status < 400) {
          try {
            const json = JSON.parse(this.response as string);
            if (
              !Object.prototype.hasOwnProperty.call(json, "chronicle_items")
            ) {
              reject(
                new Error("Invalid response, chronicle_items does not exist"),
              );
              return;
            }
            if (!Object.prototype.hasOwnProperty.call(json, "_summary")) {
              reject(new Error("Invalid response, _summary does not exist"));
              return;
            }
            resolve({
              chronicle_items:
                json.chronicle_items as Array<LocalizedChronicleItem>,
              _summary: {
                activity: (json._summary as { activity: number }).activity,
                chronicle: (json._summary as { chronicle: number }).chronicle,
              },
              _pagination:
                (json._pagination as { next_cursor: string } | undefined) ||
                undefined,
            });
          } catch (_e) {
            reject(new Error("Invalid JSON response"));
          }
        } else {
          reject(new Error(`HTTP ${this.status}: ${this.statusText}`));
        }
      };

      request.onerror = function () {
        detach();
        reject(new Error("Network error"));
      };

      request.ontimeout = function () {
        detach();
        reject(new Error("Request timeout"));
      };

      request.onabort = function () {
        detach();
      };

      request.send();
    });
  });
}

export function putItem(
  { chronicleItemId, item, productSlug, source }: PutItemParams,
  { signal, country, env }: RequestOptions = {},
): Promise<void> {
  if (signal?.aborted) {
    return Promise.reject(createAbortError());
  }

  const searchParams = source === "indexeddb" ? { source: "indexeddb" } : {};
  const requestUrl = buildChronicleUrl(
    resolveChronicleDomain({ country, env }),
    [productSlug, chronicleItemId, "v1"],
    searchParams,
  );

  return Check24.platform.auth.waitForDeviceToken().then(() => {
    if (signal?.aborted) {
      throw createAbortError();
    }

    return new Promise<void>((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open("PUT", requestUrl, true);
      request.withCredentials = true;
      request.setRequestHeader("Content-Type", "application/json");

      const detach = attachAbortSignal(request, signal, reject);

      request.onload = function () {
        detach();
        if (this.status >= 200 && this.status < 400) {
          resolve();
        } else {
          reject(new Error(`HTTP ${this.status}: ${this.statusText}`));
        }
      };

      request.onerror = function () {
        detach();
        reject(new Error("Network error"));
      };

      request.ontimeout = function () {
        detach();
        reject(new Error("Request timeout"));
      };

      request.onabort = function () {
        detach();
      };

      request.send(
        JSON.stringify({
          chronicle_item: item,
        }),
      );
    });
  });
}

export function deleteItem(
  { chronicleItemId, productSlug, sourceType }: ItemIdentifier,
  { signal, country, env }: RequestOptions = {},
): Promise<void> {
  if (signal?.aborted) {
    return Promise.reject(createAbortError());
  }

  const requestUrl = buildChronicleUrl(
    resolveChronicleDomain({ country, env }),
    [productSlug, chronicleItemId, "v1"],
    {
      source_type: sourceType,
    },
  );

  return new Promise<void>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("DELETE", requestUrl, true);
    request.withCredentials = true;

    const detach = attachAbortSignal(request, signal, reject);

    request.onload = function () {
      detach();
      if (this.status >= 200 && this.status < 400) {
        resolve();
      } else {
        reject(new Error(`HTTP ${this.status}: ${this.statusText}`));
      }
    };

    request.onerror = function () {
      detach();
      reject(new Error("Network error"));
    };

    request.ontimeout = function () {
      detach();
      reject(new Error("Request timeout"));
    };

    request.onabort = function () {
      detach();
    };

    request.send();
  });
}
